import React, { useEffect, useMemo } from 'react';

import { openBlob } from '@apis/common';
import { deliveryRequestApis } from '@apis/next/deliveryRequestApis';
import { pdfApis } from '@apis/next/pdfApis';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import { QueryContainer } from '@components/QueryContainer';
import { deliveryRequestStrings } from '@data/deliveryRequest';
import { secureDataStrings } from '@data/secureData';
import { useSearch } from '@hooks/next/useSearch';
import { DeliveryRequestHelper } from '@models/next/DeliveryRequestHelper';
import { UserHelper } from '@models/User';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { mapOptional, toJST } from '@utils/types';
import clsx from 'clsx';
import { Document, Page } from 'react-pdf';
import { z } from 'zod';

const strings = createStrings({
  ja: {
    title: `${deliveryRequestStrings.deliveryRequestFile}`,
    exportPdf: 'PDF出力',
    downloadPdfPrompt:
      '配車確定してからpdf出力してください。確定できない場合、担当者まで連絡してください。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  background: {
    backgroundColor: theme.palette.background.paper,
  },
  container: {
    ...common.flexCenter,
    margin: 16,
    marginBottom: 200,
  },
  operationPanel: {
    ...common.flexCenter,
    ...common.middleText,
    position: 'fixed',
    bottom: 0,
    background: theme.palette.primary.contrastText,
    padding: '10px 45px',

    width: '100%',
    boxShadow: '0px -4px 4px rgba(0, 0, 0, 0.25);',
  },
  operationBox: {
    ...common.flexInline,
    ...common.middleText,
    background: theme.palette.primary.contrastText,
    padding: '10px 45px',
    maxWidth: 750,
    width: '100%',
  },
  info: {
    ...common.flexColumn,
    ...common.smallerText,
    flex: 1,
    rowGap: 4,
  },
  operation: {
    ...common.flexCenter,
    columnGap: 15,
  },
}));

export const DeliveryRequestDownloadPdf: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  useEffect(() => {
    document.title = strings.title;
  }, []);

  const { search } = useSearch(
    z.object({
      delivery_request: z.string(),
      mail_read_delivery_request_access: z.string(),
      delivery_request_log_access: z.string().optional(),
    }),
  );

  const deliveryRequestQuery = deliveryRequestApis.useGet(
    '/delivery-request/secure-data/:secure_data_key',
    {
      params: { secure_data_key: search.delivery_request },
    },
  );

  const pdfQuery = pdfApis.useGet(
    '/pdf/:secure_data_key',
    {
      params: {
        secure_data_key: search.delivery_request,
      },
    },
    {
      staleTime: Infinity,
    },
  );

  const putMailRead = deliveryRequestApis.usePut('/delivery-request/mail-read/:secure_data_key', {
    params: { secure_data_key: search.mail_read_delivery_request_access },
  });

  const deliveryRequestUrl = useMemo(() => {
    if (pdfQuery.data) {
      return URL.createObjectURL(pdfQuery.data);
    }
  }, [pdfQuery.data]);

  const renderOperationPanel = () => {
    return (
      <div className={classes.operationPanel}>
        {(() => {
          if (deliveryRequestQuery.data?.state === 'ACTIVE') {
            const { data } = deliveryRequestQuery.data;

            return (
              <div className={classes.operationBox}>
                <div className={classes.info}>
                  <div
                    className={clsx(classes.textPrimary, classes.fontWeight600, classes.middleText)}
                  >
                    {strings.downloadPdfPrompt}
                  </div>

                  <div>
                    {deliveryRequestStrings.createdAt}
                    {': '}
                    {data.created_at ? toJST(new Date(data.created_at)) : '-'}
                  </div>

                  <div>
                    {deliveryRequestStrings.creator}:{' '}
                    {mapOptional(
                      data.creator,
                      (u) =>
                        (
                          <>
                            {new UserHelper(u).fullname}{' '}
                            <a className={classes.link} target="_blank" href={`mailto:${u.email}`}>
                              {u.email}
                            </a>
                          </>
                        ) ?? '-',
                    )}
                  </div>

                  <div>
                    {deliveryRequestStrings.submittedAt}:{' '}
                    {mapOptional(DeliveryRequestHelper.getMailSubmittedAt(data), (d) =>
                      toJST(new Date(d)),
                    )}
                  </div>

                  <div>
                    {deliveryRequestStrings.submissionReceiver}: {data.partner?.name_ja ?? '-'}
                  </div>

                  <div>
                    {search.delivery_request_log_access && (
                      <a
                        href={
                          url('/external/PrintDeliveryRequestLog') +
                          '?' +
                          new URLSearchParams({
                            delivery_request_log_access: search.delivery_request_log_access,
                          }).toString()
                        }
                        target="_blank"
                        className={classes.link}
                      >
                        {deliveryRequestStrings.changeLog}
                      </a>
                    )}
                  </div>
                </div>

                <div className={classes.operation}>
                  <Button
                    color="primary"
                    text={strings.exportPdf}
                    type="button"
                    shape="round"
                    size="small"
                    disabled={!pdfQuery.isSuccess}
                    onClick={() => {
                      openBlob(
                        pdfQuery.data!,
                        `${deliveryRequestStrings.deliveryRequestDocuments}.pdf`,
                        'application/pdf',
                      );
                      if (data.status.at(-1)?.name === 'メール送信済未読') {
                        putMailRead.mutate(null as never);
                      }
                    }}
                  />
                </div>
              </div>
            );
          } else if (deliveryRequestQuery.data?.state === 'CANCELLED') {
            return (
              <Alert open severity="error">
                {secureDataStrings.errorCancelled}
              </Alert>
            );
          } else if (deliveryRequestQuery.data?.state === 'OUTDATED') {
            return (
              <Alert open severity="warning">
                {secureDataStrings.errorOutdated}
              </Alert>
            );
          }
        })()}
      </div>
    );
  };

  return (
    <>
      <div className={classes.container}>
        <QueryContainer queries={[pdfQuery]}>
          <Document key={deliveryRequestUrl} file={deliveryRequestUrl} loading="">
            <Page pageNumber={1} width={900} />
            <Page pageNumber={2} width={900} />
          </Document>
        </QueryContainer>
      </div>

      {renderOperationPanel()}
    </>
  );
};
